import React, { MutableRefObject, useEffect, useRef, useState, useCallback } from 'react';
import SlateEditor from '../../../TextEditor/TextEditor';
import { Button, Modal } from '../../../_custom';
import LoadingSpinner from '../../../_custom/LoadingSpinner/LoadingSpinner';
import styles from './DealSummaryEditor.module.scss';
import {
    getSignedUrl,
    saveDocMetaDataInDocService,
    uploadFile,
} from '../../../../modules/Company/UploadJourney/components/UploadCard/utils/api/helpers';
import { toast } from 'react-toastify';
import { DATE_FORMATS, formatDateByFormat, getTimeStampForFileName } from '../../../../utils/dateUtils';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { fetchImageFromS3AndConvertToBase64 } from '../../../../utils/s3';
import { regenerateIcon, reloadTransparent } from '../../../../assets/hostedassets';
import { processImagesBeforeSave, collectDocIdsFromSlate, resolveDocIdsForPdf } from '../../../TextEditor/plugins/imageUploadService';
import { Descendant } from 'slate';
import dayjs from 'dayjs';
import { DISPATCH_EVENTS, TIME_DATE_FORMAT } from '../../../../utils/constants/commonConstants';
import { closeCommentsThread, resetAndHideCommentsThread } from '../../../../store/commentsThread/commentsThread_actions';
import { Mixpanel } from '../../../../utils/mixpanel';
import { getSubdomain } from '../../../../utils/utils';
import {
    AICA_ONEPAGER_CLOSED,
    AICA_ONEPAGER_PREVIEW,
    AICA_ONEPAGER_REGENERATE,
    AICA_ONEPAGER_VIEW_HISTORY,
    AICA_ONEPAGER_RESTORE_VERSION,
} from '../../../../utils/constants/mixpanelEvents/investorEvents';
import { TEXT_EDITOR_TYPES } from '../../../../store/investor/constants';
import { saveOnePagerTemplateData } from '../../../../store/investor/investorApiHelper';
import { pdf } from '@react-pdf/renderer';
// ReactPdfDocument is dynamically imported in handlePreview and handleSaveToDb for code splitting
import { sanitizeSlateValue } from '../../../TextEditor/utils/SlateSerialisationUtil';
import TextEditorShimmerLoader from '../../../TextEditor/TextEditorShimmerLoader';
import { CREATE_DEAL } from '../../../../utils/constants/commonConstants';
import HistorySideDrawer from '../../../HistorySideDrawer/HistorySideDrawer';
import { useDocumentHistory } from '../../../HistorySideDrawer/useDocumentHistory';

function DealSummaryEditor({
    source,
    openModal,
    closeModal,
    categoryId,
    flowId,
    assesseeOrgId,
    metadataUpdate,
    generating,
    editorInitialData,
    editorReady,
    savingData,
    saveOnePagerTemplateDataForOrg,
    setShowRegenConfirmModal,
    userInfo,
    companyNameProp,
}) {
    const dispatch = useAppDispatch();
    const [saving, setSaving] = useState(false);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const contentToPrint = useRef(null);
    const editorContent = useRef<Descendant[]>([]);
    const companyName = companyNameProp || useAppSelector((state) => state.investor.dealApplicationDrawer.subHeading);
    const domainMetadata = useAppSelector((state) => state.user.domainMetadata);
    const user = useAppSelector((state) => state.user.userData);
    const mainImageUrl = domainMetadata?.mainImageUrl;
    const orgName = domainMetadata?.orgName;
    const [triggerSave, setTriggerSave] = useState(false);
    const [logoBase64, setLogoBase64] = useState<string | undefined>(undefined);
    const [currentEditorData, setCurrentEditorData] = useState(editorInitialData);
    const originalDocIdsRef = useRef<string[]>([]);
    const [showExternalRestoreModal, setShowExternalRestoreModal] = useState(false);

    const getMixpanelProperties = useCallback(
        () => ({
            currentUrl: window.location.href,
            pnoId: domainMetadata?.investorOrgId,
            orgId: assesseeOrgId,
            userEmail: user?.primaryEmail,
        }),
        [domainMetadata?.investorOrgId, assesseeOrgId, user?.primaryEmail],
    );

    // Persist content as a new version. Owned save path used by the shared history hook
    // (auto-save before drawer open + restore flow) and by the external ONEPAGER_AUTOSAVE event.
    const persistVersion = async (content: Descendant[], opts?: { isRestored?: boolean }) => {
        if (!assesseeOrgId || !user.investorOrgId) return;
        const processedContent = await processImagesBeforeSave(content, originalDocIdsRef.current, 'one-pager');
        await saveOnePagerTemplateData({
            assesseeOrgId,
            investorOrgId: user.investorOrgId,
            onePagerData: processedContent,
            email: user.primaryEmail,
            isRestored: opts?.isRestored,
        });
        originalDocIdsRef.current = collectDocIdsFromSlate(processedContent);
    };

    // Shared history-drawer integration (auto-save before open, version load/preview/restore,
    // editor remount via key bump, lastSaved snapshot).
    // NOTE: docType is the backend enum literal 'ONE_PAGER' — must match FreeTextDocumentType
    // enum on aica. TEXT_EDITOR_TYPES.ONE_PAGER is 'OnePager' (camelCase frontend label), which
    // would fail Spring's enum binding on /data/getAllOnePagerData.
    const history = useDocumentHistory({
        docType: 'ONE_PAGER',
        investorOrgId: user.investorOrgId,
        assesseeOrgId: assesseeOrgId as string,
        editorContentRef: editorContent,
        onLoadVersion: setCurrentEditorData,
        persistVersion,
        onHistoryViewed: () => Mixpanel.track(AICA_ONEPAGER_VIEW_HISTORY, getMixpanelProperties()),
    });

    const handleRestoreClick = (entry: any) => {
        setShowExternalRestoreModal(true);
    };

    const handleConfirmExternalRestore = () => {
        const selectedEntry = history.historyEntries.find((entry) => entry._id === history.selectedHistoryEntryId);
        if (selectedEntry) {
            // Track restore event
            Mixpanel.track(AICA_ONEPAGER_RESTORE_VERSION, getMixpanelProperties());
            history.selectHistoryEntry(selectedEntry);
        }
        setShowExternalRestoreModal(false);
    };

    // Callback for Mixpanel tracking when restore is confirmed (from three-dot menu)
    const handleRestoreConfirmTracking = useCallback(() => {
        Mixpanel.track(AICA_ONEPAGER_RESTORE_VERSION, getMixpanelProperties());
    }, [getMixpanelProperties]);

    useEffect(() => {
        dispatch(resetAndHideCommentsThread());
        const handleAutosave = async () => {
            if (!editorContent.current?.length) return;
            try {
                await persistVersion(editorContent.current);
                history.markAsSaved(editorContent.current);
            } catch (err) {
                console.error('Error saving template:', err);
            }
        };
        const events = { [DISPATCH_EVENTS.ONEPAGER_AUTOSAVE]: handleAutosave };
        Object.entries(events).forEach(([event, handler]) => addEventListener(event, handler));
        return () => Object.entries(events).forEach(([event, handler]) => removeEventListener(event, handler));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        // Update current editor data when initial data changes (from fetching, regeneration, etc.)
        if (editorInitialData && history.editorKey === 0) {
            setCurrentEditorData(editorInitialData);
            history.markAsSaved(editorInitialData);
            originalDocIdsRef.current = collectDocIdsFromSlate(editorInitialData);
        }
    }, [editorInitialData, history.editorKey, history.markAsSaved]);

    useEffect(() => {
        if (triggerSave) {
            setTriggerSave(false);
        }
    }, [triggerSave, editorContent, assesseeOrgId]);

    // Load logo on mount
    useEffect(() => {
        const loadLogo = async () => {
            if (mainImageUrl) {
                try {
                    const response = await fetchImageFromS3AndConvertToBase64(mainImageUrl);
                    if (response) {
                        setLogoBase64(response as string);
                    }
                } catch (err: any) {
                    console.error('Error loading logo:', err);
                }
            }
        };
        loadLogo();
    }, [mainImageUrl]);

    /**
     * Handles PDF preview - generates PDF and opens in new tab
     * Opens blank tab synchronously to avoid popup blockers
     */
    const handlePreview = async () => {
        setLoadingPreview(true);
        // Open blank tab synchronously before async work to avoid popup blockers
        const previewWindow = window.open('about:blank', '_blank');

        try {
            // Sanitize content and resolve docId images to presigned URLs for PDF rendering
            const rawContent = editorContent.current?.length ? editorContent.current : editorInitialData ?? [];
            const sanitized = sanitizeSlateValue(rawContent);

            if (!sanitized || sanitized.length === 0) {
                toast.error('No content to preview');
                if (previewWindow) {
                    previewWindow.close();
                }
                setLoadingPreview(false);
                return;
            }

            const content = await resolveDocIdsForPdf(sanitized);
            const { default: ReactPdfDocumentComponent } = await import('../../../TextEditor/print/ReactPdfDocument');

            const pdfDocument = (
                <ReactPdfDocumentComponent
                    value={content}
                    pageSize="A3"
                    logoBase64={logoBase64}
                    orgName={orgName}
                    companyName={companyName}
                    includeWatermark={true}
                    documentType="onePager"
                />
            );

            const blob = await pdf(pdfDocument).toBlob();
            const url = URL.createObjectURL(blob);

            if (previewWindow) {
                // Assign URL to the already-open tab
                previewWindow.location.href = url;
            } else {
                // Fallback: create a link and click it
                const link = document.createElement('a');
                link.href = url;
                link.target = '_blank';
                link.click();
            }

            // Clean up URL after delay
            setTimeout(() => URL.revokeObjectURL(url), 30000);
        } catch (err: any) {
            console.error('Error generating PDF preview:', err);
            toast.error(`Error generating PDF preview: ${err?.message || 'Unknown error'}`);
            if (previewWindow) {
                previewWindow.close();
            }
        } finally {
            setLoadingPreview(false);
        }
    };

    /**
     * Handles PDF save to database using @react-pdf/renderer
     * Generates PDF blob and uploads to S3
     */
    const handleSaveToDb = async () => {
        try {
            // Sanitize content and resolve docId images to presigned URLs for PDF rendering
            const rawContent = editorContent.current?.length ? editorContent.current : editorInitialData ?? [];
            const content = await resolveDocIdsForPdf(sanitizeSlateValue(rawContent));

            const { default: ReactPdfDocument } = await import('../../../TextEditor/print/ReactPdfDocument');

            const pdfDocument = (
                <ReactPdfDocument
                    value={content}
                    pageSize="A3"
                    logoBase64={logoBase64}
                    orgName={orgName}
                    companyName={companyName}
                    includeWatermark={true}
                    documentType="onePager"
                />
            );

            const blob = await pdf(pdfDocument).toBlob();
            const filename = `${companyName ? companyName + '-' : ''}ONE_PAGER.pdf`;
            const file = new File([blob], filename, { type: 'application/pdf' });

            await getSignedUrlFromS3(file);
        } catch (err: any) {
            console.error('Error saving PDF:', err);
            setSaving(false);
            toast.error(`Error saving PDF: ${err?.message || 'Unknown error'}`);
        }
    };

    const getSignedUrlFromS3 = async (file) => {
        const uploadConfig = {
            assesseeOrgId: assesseeOrgId,
            categoryId: categoryId,
            mnemosyneOrgId: domainMetadata?.investorOrgId,
        };
        const fileData = {
            file: {
                name: `${companyName ? companyName + '-' : ''} Brief Profile - ${getTimeStampForFileName()}.pdf`,
                type: 'application/pdf',
                size: file.size,
            },
        };
        try {
            const signResponse = await getSignedUrl(fileData, uploadConfig);
            await uploadFileAfter(signResponse.signedUrl, file, fileData);
        } catch (err) {
            setSaving(false);
            // Error getting signed URL from S3 - handled by error state
            toast.error('Some error occoured, please try again later');
        }
    };

    const handleClose = () => {
        dispatch(closeCommentsThread());
        if (source !== CREATE_DEAL) {
            setTriggerSave(true);
            saveOnePagerTemplateDataForOrg(editorContent.current);
        } else if (source === CREATE_DEAL) {
            setSaving(true);
            handleSaveToDb();
        } else {
            closeModal();
        }

        Mixpanel.track(AICA_ONEPAGER_CLOSED, {
            userRole: user?.accountType,
            pnoPlatform: getSubdomain(),
            pnoId: domainMetadata?.investorOrgId,
            userId: user?.userId,
            currentUrl: window.location.href,
            organizationId: assesseeOrgId,
        });
    };

    const uploadFileAfter = async (url, file, fileData) => {
        const uploadResponse = await uploadFile(file, url);
        const input_saveMetadata = {
            categoryId: categoryId,
            mnemosyneOrgId: domainMetadata?.investorOrgId,
            fileName: fileData.file.name,
            fileSizeKb: fileData?.file?.size / 1_000,
            flowId: flowId,
            fileType: fileData.file.type,
        };
        const docData = await saveDocMetaDataInDocService({
            input: input_saveMetadata,
            orgId: assesseeOrgId,
        });

        const fileInfo = {
            url: uploadResponse?.url,
            fileName: fileData.file.name,
            docId: docData?.id,
        };
        metadataUpdate(fileInfo);
        setSaving(false);
        toast.success('Successfully Saved');
        closeModal();
    };

    function generateUserMessage(userInfo: { userName: string; onePagerData: { lastUpdatedAt: string } }) {
        if (!userInfo?.userName || !userInfo?.onePagerData?.lastUpdatedAt) {
            return '';
        }
        const formattedDate = dayjs(userInfo.onePagerData.lastUpdatedAt).format(TIME_DATE_FORMAT);

        return `Last edited on ${formattedDate} by ${userInfo.userName}`;
    }

    const handlePreviewClick = (e) => {
        e.preventDefault();
        handlePreview();

        Mixpanel.track(AICA_ONEPAGER_PREVIEW, {
            userRole: user?.accountType,
            pnoPlatform: getSubdomain(),
            pnoId: domainMetadata?.investorOrgId,
            userId: user?.userId,
            currentUrl: window.location.href,
            organizationId: assesseeOrgId,
        });
    };

    const handleSaveAndClose = async (e) => {
        e.preventDefault();
        setSaving(true);
        setTriggerSave(true);
        dispatch(closeCommentsThread());
        if (source === CREATE_DEAL) {
            // Save template in background (does NOT close the modal)
            if (editorContent.current?.length) {
                persistVersion(editorContent.current)
                    .then(() => history.markAsSaved(editorContent.current))
                    .catch((err) => console.error('Error saving template:', err));
            }
            // Wait for the full PDF save chain — ensures docId is received before modal closes
            await handleSaveToDb();
        } else {
            const processedContent = await processImagesBeforeSave(editorContent.current, originalDocIdsRef.current, 'one-pager');
            saveOnePagerTemplateDataForOrg(processedContent);
        }

        Mixpanel.track(AICA_ONEPAGER_CLOSED, {
            userRole: user?.accountType,
            pnoPlatform: getSubdomain(),
            pnoId: domainMetadata?.investorOrgId,
            userId: user?.userId,
            currentUrl: window.location.href,
            organizationId: assesseeOrgId,
        });
    };

    // Add loading state UI for regeneration
    const renderRegenerateButton = () => {
        return (
            <div
                className={styles.Regen}
                onClick={() => {
                    if (!generating) {
                        setShowRegenConfirmModal(true);

                        Mixpanel.track(AICA_ONEPAGER_REGENERATE, {
                            userRole: user?.accountType,
                            pnoPlatform: getSubdomain(),
                            pnoId: domainMetadata?.investorOrgId,
                            userId: user?.userId,
                            currentUrl: window.location.href,
                            organizationId: assesseeOrgId,
                        });
                    }
                }}
            >
                {generating ? (
                    <>
                        <LoadingSpinner color="var(--primary-text-colour)" height="18px" style={{ margin: 0 }} />
                        <span>Regenerating</span>
                    </>
                ) : (
                    <>
                        <img src={regenerateIcon} alt={'re'} height={'24px'} />
                        <span>Regenerate</span>
                    </>
                )}
            </div>
        );
    };

    return openModal ? (
        <>
            <Modal
                size="full"
                open={openModal}
                style={{ padding: 0, fontSize: '12px', lineHeight: '140%', position: 'absolute' }}
                persistent
                withoutCloseButton
            >
                <div className={styles.Container}>
                    <div className={styles.Top} style={!editorReady ? { display: 'flex', flexDirection: 'column' } : undefined}>
                        {!editorReady && (
                            <div style={{ flex: 1 }}>
                                <TextEditorShimmerLoader />
                            </div>
                        )}
                        {!!editorReady && (
                            <SlateEditor
                                key={history.editorKey}
                                source={source}
                                editorInitialData={currentEditorData}
                                targetRef={contentToPrint}
                                editorContentRef={editorContent}
                                assesseeOrgId={assesseeOrgId}
                                generating={generating || history.previewLoading}
                                onClose={handleClose}
                                savingData={savingData || saving}
                                editorType={TEXT_EDITOR_TYPES.ONE_PAGER}
                                onToggleHistory={history.handleToggleHistory}
                                historyOpen={history.isOpen}
                                readOnly={history.isOpen}
                            />
                        )}
                        {!!editorReady && (
                            <div className={styles.Footer}>
                                {!history.isOpen && <div>{renderRegenerateButton()}</div>}

                                {!history.isOpen && (
                                    <div className={styles.Text}>*Right-click tables to modify: add rows, add columns, or delete</div>
                                )}

                                {history.isOpen && history.selectedHistoryEntryId ? (
                                    <div className={styles.Text} style={{ textAlign: 'left' }}>
                                        Last edited on{' '}
                                        {formatDateByFormat(
                                            history.historyEntries.find((entry) => entry._id === history.selectedHistoryEntryId)
                                                ?.lastUpdatedAt,
                                            DATE_FORMATS.TIME_DATE_FORMAT_HH_MM_A_DD_MMM_YYYY,
                                        )}{' '}
                                        by {history.historyEntries.find((entry) => entry._id === history.selectedHistoryEntryId)?.userName}
                                    </div>
                                ) : null}

                                {!history.isOpen && <div className={styles.Text}>{generateUserMessage(userInfo)}</div>}

                                <div className={styles.Buttons}>
                                    {history.isOpen && history.selectedHistoryEntryId ? (
                                        (() => {
                                            const selectedIndex = history.historyEntries.findIndex(
                                                (entry) => entry._id === history.selectedHistoryEntryId,
                                            );
                                            const isLatestVersion = selectedIndex === 0;

                                            if (isLatestVersion) {
                                                return null; // Don't show restore button for current version
                                            }

                                            return (
                                                <Button
                                                    text="Restore this version"
                                                    variant="primary"
                                                    onClick={() => {
                                                        const selectedEntry = history.historyEntries.find(
                                                            (entry) => entry._id === history.selectedHistoryEntryId,
                                                        );
                                                        if (selectedEntry) {
                                                            handleRestoreClick(selectedEntry);
                                                        }
                                                    }}
                                                    style={{ marginTop: '0' }}
                                                    loading={history.previewLoading}
                                                    icon={reloadTransparent}
                                                />
                                            );
                                        })()
                                    ) : !history.isOpen ? (
                                        <>
                                            <Button
                                                text="Preview"
                                                variant="purpleinverted"
                                                onClick={handlePreviewClick}
                                                style={{ margin: '0 20px 0 0 ' }}
                                                loading={loadingPreview}
                                            />
                                            <Button
                                                text="Save & Close"
                                                variant="primary"
                                                onClick={handleSaveAndClose}
                                                style={{ marginTop: '0' }}
                                                loading={saving}
                                            />
                                        </>
                                    ) : null}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={styles.Comments} id="onepager-comment-container">
                        {/* //Container Element Where comments thread is rendered via portal */}
                    </div>

                    <div className={styles.History} id="onepager-history-container">
                        {/* //Container Element Where history drawer is rendered via portal */}
                    </div>
                </div>

                <HistorySideDrawer
                    containerId="onepager-history-container"
                    isOpen={history.isOpen}
                    isExpanded={history.isExpanded}
                    title="Version History"
                    loading={history.loading}
                    previewLoading={history.previewLoading}
                    historyEntries={history.historyEntries}
                    onClose={history.closeDrawer}
                    onToggleExpand={history.toggleExpand}
                    onSelectEntry={history.selectHistoryEntry}
                    onPreviewEntry={history.previewHistoryEntry}
                    selectedEntryId={history.selectedHistoryEntryId}
                    onRestoreClick={handleRestoreClick}
                    onRestoreConfirm={handleRestoreConfirmTracking}
                />

                {showExternalRestoreModal && history.selectedHistoryEntryId && (
                    <Modal open={showExternalRestoreModal} onClose={() => setShowExternalRestoreModal(false)} size="flexible">
                        <div className={styles.ModalContainer}>
                            <div className={styles.ModalTitle}>Restore this version?</div>
                            <div className={styles.ModalSubtext}>
                                Are you sure you want to restore the version from{' '}
                                {formatDateByFormat(
                                    history.historyEntries.find((entry) => entry._id === history.selectedHistoryEntryId)?.lastUpdatedAt,
                                    DATE_FORMATS.TIME_DATE_FORMAT_HH_MM_A_DD_MMM_YYYY,
                                )}
                                ?
                            </div>
                            <div className={styles.ModalButtons}>
                                <Button
                                    text="Cancel"
                                    onClick={() => setShowExternalRestoreModal(false)}
                                    variant="purpleinverted"
                                    style={{ marginTop: '0' }}
                                />
                                <Button
                                    text="Restore"
                                    onClick={handleConfirmExternalRestore}
                                    variant="primary"
                                    style={{ marginTop: '0' }}
                                    loading={history.previewLoading}
                                />
                            </div>
                        </div>
                    </Modal>
                )}
            </Modal>
        </>
    ) : (
        <></>
    );
}

export default DealSummaryEditor;
